import { useClientEmployeeQuery } from "@graphql/client-employees/clientEmployees.g";
import { RouteObject, UIMatch, redirect } from "react-router";

import { queryClient } from "@api/queries";

import { CurrentClientName } from "@components/Texts";

import { PortalPage } from "@layouts/PortalPage/PortalPage.layout";
import { ExtensionInstallationGuard } from "@components/RouteGuards/ExtensionInstallationGuard/ExtensionInstallationGuard";

import { routes } from "@pages/routes";

import { ClientEmployee } from "./ClientEmployee/ClientEmployee.page";
import { ClientEmployeeDashboard } from "./ClientEmployeeDashboard/ClientEmployeeDashboard.page";
import { ClientEmployeeFileUploads } from "./ClientEmployeeFileUploads/ClientEmployeeFileUploads.page";
import { ClientEmployeeOwnedApplications } from "./ClientEmployeeOwnedApplications/ClientEmployeeOwnedApplications.page";
import { ClientEmployeeRecord } from "./ClientEmployeeRecord/ClientEmployeeRecord.page";
import { ClientEmployees } from "./ClientEmployees/ClientEmployees.page";
import { CreateClientEmployee } from "./CreateClientEmployee/CreateClientEmployee.page";

export const clientEmployeeRoutes = [
  {
    path: routes.portal.clients.client.clientEmployees.$path(),
    Component: PortalPage,
    handle: {
      breadcrumb: ({ params }: UIMatch) => ({
        reset: true,
        title: <CurrentClientName />,
        path: routes.portal.clients.client.dashboard.$buildPath({
          params: { clientId: params.clientId as string },
        }),
      }),
    },
    children: [
      {
        Component: ExtensionInstallationGuard,
        handle: {
          breadcrumb: {
            title: "Users",
          },
        },
        children: [
          {
            index: true,
            Component: ClientEmployees,
            handle: { fullBleed: true },
          },
          {
            path: routes.portal.clients.client.clientEmployees.clientEmployee.$path(),
            Component: ClientEmployee,
            handle: {
              breadcrumb: async ({
                params: { clientId = "", clientEmployeeId = "" },
              }) => {
                const data = await queryClient.fetchQuery({
                  queryKey: useClientEmployeeQuery.getKey({
                    clientId,
                    clientEmployeeId,
                  }),
                  queryFn: useClientEmployeeQuery.fetcher({
                    clientId,
                    clientEmployeeId,
                  }),
                });

                return {
                  title: data?.clientEmployee?.name,
                };
              },
            },
            children: [
              {
                index: true,
                loader: ({ params: { clientId = "", clientEmployeeId = "" } }) => {
                  return redirect(
                    routes.portal.clients.client.clientEmployees.clientEmployee.dashboard.$buildPath(
                      {
                        params: { clientId, clientEmployeeId },
                      },
                    ),
                  );
                },
              },
              {
                path: routes.portal.clients.client.clientEmployees.clientEmployee.dashboard.$path(),
                Component: ClientEmployeeDashboard,
                handle: {
                  breadcrumb: {
                    title: "Dashboard",
                  },
                },
              },
              {
                path: routes.portal.clients.client.clientEmployees.clientEmployee.record.$path(),
                Component: ClientEmployeeRecord,
                handle: {
                  breadcrumb: {
                    title: "Record",
                  },
                },
              },
              {
                path: routes.portal.clients.client.clientEmployees.clientEmployee.ownedApplications.$path(),
                Component: ClientEmployeeOwnedApplications,
                handle: {
                  fullBleed: true,
                  breadcrumb: {
                    title: "Owned applications",
                  },
                },
              },
              {
                path: routes.portal.clients.client.clientEmployees.clientEmployee.fileUploads.$path(),
                Component: ClientEmployeeFileUploads,
                handle: {
                  fullBleed: true,
                  breadcrumb: {
                    title: "File Uploads",
                  },
                },
              },
            ],
          },
          {
            path: routes.portal.clients.client.clientEmployees.create.$path(),
            Component: CreateClientEmployee,
            handle: {
              breadcrumb: {
                title: "Create",
              },
            },
          },
        ],
      },
    ],
  },
] satisfies RouteObject[];
